import { FieldSelect, FieldText, Form, FormikForm, FormRowField, Label } from "@skilitics/designer-ui-components";
import type { ArrayBinaryOperator } from "@skilitics/judy";
import { useFormatMessage } from "@skilitics/localization";

import type { StringArrayConditionContext } from "./types";
import { stringArrayOperators, useStringArrayOperatorLabels } from "./useStringArrayOperatorLabels";

export type StringArrayConditionFormValues = { operator: ArrayBinaryOperator | ""; value: string };

export type StringArrayConditionFormProps = Pick<StringArrayConditionContext["spec"], "options"> & {
  initialValues: StringArrayConditionFormValues;
  onSubmit: (newValues: StringArrayConditionFormValues) => void;
  specOperator: StringArrayConditionContext["spec"]["operator"];
};

export const StringArrayConditionForm = ({
  initialValues,
  onSubmit,
  options,
  specOperator,
}: StringArrayConditionFormProps) => {
  const formatMessage = useFormatMessage(formatStrings);
  const operatorLabels = useStringArrayOperatorLabels();
  const operatorOptions = stringArrayOperators.map((value) => ({
    value,
    label: operatorLabels[value].label,
  }));
  const valueOptions = options?.map(({ value, label }) => ({ value, label: label ?? value }));

  return (
    <FormikForm<StringArrayConditionFormValues>
      initialValues={initialValues}
      onSubmit={(values) => onSubmit(values)}
      submitOnBlur
      dataTest="string-array-condition-form"
    >
      <Form $labelAlignment="inline">
        <FormRowField<StringArrayConditionFormValues> name="operator">
          <Label>{formatMessage("operatorLabel")}</Label>
          <FieldSelect disabled={!!specOperator} options={operatorOptions} />
        </FormRowField>

        <FormRowField<StringArrayConditionFormValues> name="value">
          <Label>{formatMessage("valueLabel")}</Label>
          {valueOptions && <FieldSelect options={valueOptions} />}
          {!valueOptions && <FieldText />}
        </FormRowField>
      </Form>
    </FormikForm>
  );
};

const formatStrings = {
  operatorLabel: {
    en: "Operator",
  },
  valueLabel: {
    en: "Value",
  },
};
